/* eslint-disable no-nested-ternary */
/* eslint-disable react/jsx-no-undef */
import React, { useState, useEffect, useCallback } from 'react';
import { PropTypes } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
  Checkbox,
  DatePicker,
  Upload,
  Collapse,
  Input,
  Row,
  Col,
  Select,
  Spin,
  List,
  Button,
  Progress,
  InputNumber,
  Form,
  Space,
} from 'antd';
import {
  UploadOutlined,
  FilePdfTwoTone,
  LoadingOutlined,
  PlusOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';

import { debounce } from 'lodash';
import {
  searchMerchantAccounts,
  updateMerchantAccount,
  searchProcessors,
  fetchMerchantAccounts,
} from '../../redux/actions/Data';
import { documentTypes } from './DocumentTypes';
import s3, { bucketBaseURL } from '../../util/s3';
import { getAllUsers } from '../../redux/actions/Auth';
import TagInput from '../../components/TagInput';

const { Option } = Select;
const { Panel } = Collapse;
const formItemLayout = {
  labelCol: {
    span: 12,
  },
  wrapperCol: {
    span: 12,
  },
};

const MerchantForm = ({
  form,
  editable,
  selectedMerchanTypes,
  selectedBlacklistedProcessors,
  handleChange,
  handleChangeWebsites,
  handleChangeBlacklistedProcessors,
  websites,
}) => {
  const dispatch = useDispatch();
  const [selectedUser, setselectedUser] = useState('');
  const [selectedMerchant, setselectedMerchant] = useState('');
  const [progress, setProgress] = useState(0);
  const [currentDocument, setCurrentDocument] = useState('');
  const { data: userData, loading } = useSelector(({ auth }) => auth.users);
  const { loadingAuthUser, authUser } = useSelector(({ auth }) => auth);
  const { data: merchants } = useSelector(({ data }) => data.searchMerchantAccounts);
  const record = useSelector(({ data }) => data.selectedMerchantAccount);
  const isAffilate = record?.enable_affiliate;
  const isMerchant = authUser.role.name.includes('Merchant');
  const processors = useSelector(({ data }) => data.searchProcessors);

  const handleIconRender = (file) => {
    const icon = file.status === 'uploading' ? <LoadingOutlined /> : <FilePdfTwoTone />;
    return icon;
  };

  const userSearch = (search) => {
    const searchFilter = JSON.stringify([
      { full_name: { $regex: search.trim(), $options: 'i' } },
      { first_name: { $regex: search.trim(), $options: 'i' } },
      { last_name: { $regex: search.trim(), $options: 'i' } },
      { email: { $regex: search.trim(), $options: 'i' } },
    ]);
    const searchParams = `filter={"$or": ${searchFilter}}`;
    dispatch(getAllUsers(searchParams));
  };

  const merchantSearch = (search) => {
    const searchFilter = JSON.stringify([
      { name: { $regex: search.trim(), $options: 'i' } },
    ]);
    const searchParams = `filter={"$or": ${searchFilter}}`;
    dispatch(fetchMerchantAccounts(searchParams, true));
  };

  const debouncedSearch = useCallback(
    debounce((nextValue) => userSearch(nextValue), 1000),
    [],
  );

  const handleUserSearch = (value) => {
    debouncedSearch(value);
  };

  const debouncedMerchantSearch = useCallback(
    debounce((nextValue) => merchantSearch(nextValue), 1000),
    [],
  );

  const handleMerchantSearch = (value) => {
    debouncedMerchantSearch(value);
  };

  useEffect(() => {
    const params = 'page=1&limit=8';
    dispatch(getAllUsers('', false));
    dispatch(searchMerchantAccounts(params));
    dispatch(searchProcessors());
    dispatch(fetchMerchantAccounts(null, true));
  }, [dispatch]);

  const usersOptions = userData && userData
    .map((user) => (<Option key={user._id}>{`${user.full_name} - ${user.email}`}</Option>));

  const merchantOptions = merchants && merchants.filter((merchant) => merchant._id !== record?._id)
    .map((merchant) => (<Option key={merchant._id}>{merchant.name}</Option>));

  const processorOptions = processors && processors.map((processor) => (
    <Option key={processor._id}>{processor.name}</Option>));

  const uploadProps = {
    name: 'file',
    accept: '.pdf',
    listType: 'picture-card',
    multiple: false,
    maxCount: 1,
    showUploadList: {
      showPreviewIcon: false,
      showRemoveIcon: true,
    },
    iconRender: handleIconRender,
    customRequest({
      file,
      onError,
      data,
      onSuccess,
      onProgress,
    }) {
      const bucketSubFolder = (data.key === 'contract') ? 'contract' : 'kyc';
      const bucketParams = {
        Bucket: process.env.REACT_APP_AWS_BUCKET,
        Key: `kitegateway/${bucketSubFolder}/${record._id}/${data.key}.pdf`,
        Body: file,
        ContentType: 'application/pdf',
        ContentDisposition: 'inline',
      };

      setCurrentDocument(data.key);
      s3.putObject(bucketParams)
        .on('httpUploadProgress', ({ loaded, total }) => {
          const percent = Math.round((loaded / total) * 100);
          onProgress({ percent }, file);
          setProgress(percent);
          if (percent === 100) {
            setTimeout(() => setProgress(0), 1000);
          }
        })
        .send((error, response) => {
          if (error) {
            onError();
            console.error(error);
          } else {
            onSuccess(response, file);
            const merchantId = record && record._id;
            const fileLink = `${bucketBaseURL}kitegateway/${bucketSubFolder}/${merchantId}/${data.key}.pdf`;
            const postData = {
              [data.key]: fileLink,
            };
            dispatch(updateMerchantAccount(merchantId, postData, false));
          }
        });
    },
  };

  const handleRemoveFile = (documentName) => {
    const bucketSubFolder = (documentName === 'contract') ? 'contract' : 'kyc';
    const bucketParams = {
      Bucket: process.env.REACT_APP_AWS_BUCKET,
      Key: `kitegateway/${bucketSubFolder}/${record._id}/${documentName}.pdf`,
    };
    s3.deleteObject(bucketParams, (error) => {
      if (error) {
        console.error(error);
      } else {
        const merchantId = record && record._id;
        const postData = {
          [documentName]: '',
        };
        dispatch(updateMerchantAccount(merchantId, postData, false));
      }
    });
  };

  return (
    <Form
      name="merchant-form"
      className="gx-form-row0"
      ref={form}
      {...formItemLayout}
      initialValues={{
        card_settlement_period: 7,
        setup_fees: 0,
        monthly_fees: 0,
        rolling_reserve: 10,
        rolling_reserve_period: 6,
      }}
    >
      <Row>
        <Col xl={15} lg={15} md={12} sm={24} xs={12}>
          <Form.Item
            rules={[
              { required: true, message: 'This input is required!' },
            ]}
            name="name"
            label="Name"
          >
            <Input placeholder="Name" />
          </Form.Item>
          <Form.Item
            label="Websites"
            name="websites"
          >
            <Select
              mode="tags"
              style={{ width: '100%' }}
              placeholder="Add websites"
            />
          </Form.Item>
          {!isMerchant && (
            <Form.Item
              name="user"
              label="Merchant User"
              rules={[
                { required: true, message: 'This input is required!' },
              ]}
            >
              <Select
                showSearch
                value={selectedUser}
                placeholder="Search for a user by email"
                showArrow={false}
                filterOption={false}
                onSearch={handleUserSearch}
                onChange={(value) => setselectedUser(value)}
                notFoundContent={loading ? <Spin size="small" /> : 'No User found'}
              >
                {usersOptions}
              </Select>
            </Form.Item>
          )}
          {!isMerchant && (
            <Form.Item
              name="parent_merchant"
              label="Parent Merchant"
            >
              <Select
                showSearch
                value={selectedMerchant}
                placeholder="Search for a merchant by name"
                showArrow={false}
                filterOption={false}
                onSearch={handleMerchantSearch}
                onChange={(value) => setselectedMerchant(value)}
                notFoundContent={loading ? <Spin size="small" /> : 'No merhant found'}
              >
                {merchantOptions}
              </Select>
            </Form.Item>
          )}
          {!isMerchant && (
            <>
              <Form.Item
                label="RR (%)"
                name="rolling_reserve"
              >
                <InputNumber
                  min={10}
                  style={{ width: '100%' }}
                  step={5}
                />
              </Form.Item>
              <Form.Item
                label="RR (Months)"
                name="rolling_reserve_period"
              >
                <InputNumber
                  min={3}
                  style={{ width: '100%' }}
                  step={1}
                />
              </Form.Item>
              <Form.Item
                label="Card T+ (Days)"
                name="card_settlement_period"
              >
                <InputNumber
                  min={3}
                  style={{ width: '100%' }}
                  step={1}
                />
              </Form.Item>
              <Form.Item
                label="Card Velocity Period (Hours)"
                name="card_velocity_period"
              >
                <InputNumber
                  min={1}
                  style={{ width: '100%' }}
                  step={1}
                />
              </Form.Item>
            </>
          )}
        </Col>
        <Col xl={9} lg={9} md={12} sm={24} xs={12}>
          <Form.Item
            label="Active"
            name="is_active"
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>
          <Form.Item
            label="Collections Support"
            name="support_collections"
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>
          <Form.Item
            label="Disbursements Support"
            name="support_disbursements"
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>
          <Form.Item
            label="Crypto Support"
            name="support_crypto"
            valuePropName="checked"
          >
            <Checkbox />
          </Form.Item>
          {!isMerchant && (
            <>
              <Form.Item
                label="Enable Affiliate"
                name="enable_affiliate"
                valuePropName="checked"
              >
                <Checkbox />
              </Form.Item>
              <Form.Item
                label="Allow Restricted Countries"
                name="allow_restricted_countries"
                valuePropName="checked"
              >
                <Checkbox />
              </Form.Item>
            </>
          )}
          {/* <Form.Item
            label="Setup Fees"
            name="setup_fees"
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Monthly Fees"
            name="monthly_fees"
          >
            <Input />
          </Form.Item> */}
        </Col>
      </Row>
      {!isMerchant && (
        <>
          <Collapse accordion>
            <Collapse.Panel header="Daily Limits" key="daily_limits">
              <Form.List name="daily_limits" style={{ width: '100%' }}>
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Space
                        key={key}
                        style={{
                          display: 'flex',
                          marginBottom: 8,
                        }}
                        align="baseline"
                      >
                        <Form.Item
                          {...restField}
                          name={[name, 'transaction_type']}
                          rules={[
                            {
                              required: true,
                              message: 'Transaction type is required!',
                            },
                          ]}
                          // noStyle
                          noStyle
                        >
                          <Select
                            style={{ width: '100%' }}
                          >
                            <Option key="COLLECTION">COLLECTION</Option>
                            <Option key="DISBURSEMENT">DISBURSEMENT</Option>
                          </Select>
                        </Form.Item>
                        <Form.Item
                          {...restField}
                          name={[name, 'method']}
                          rules={[
                            {
                              required: true,
                              message: 'Payment method is required!',
                            },
                          ]}
                          // noStyle
                          noStyle
                        >
                          <Select
                            style={{ width: '100%' }}
                          >
                            <Option key="CARD">CARD</Option>
                            <Option key="BANK">BANK</Option>
                            <Option key="MOBILE_MONEY">MOBILE_MONEY</Option>
                          </Select>
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, 'currency']}
                          rules={[
                            {
                              required: true,
                              message: 'Currency is required!',
                            },
                          ]}
                          // noStyle
                          noStyle
                        >
                          <Select
                            style={{ width: '100%' }}
                          >
                            <Option key="USD">USD</Option>
                            <Option key="EUR">EUR</Option>
                            <Option key="UGX">UGX</Option>
                            <Option key="KES">KES</Option>
                          </Select>
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, 'amount']}
                          rules={[
                            {
                              required: true,
                              message: 'Amount is required!',
                            },
                          ]}
                          noStyle
                        >
                          <Input placeholder="Amount" />
                        </Form.Item>
                        <MinusCircleOutlined onClick={() => remove(name)} />
                      </Space>
                    ))}
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Add daily limit
                    </Button>
                  </>
                )}
              </Form.List>
            </Collapse.Panel>
          </Collapse>
        </>
      )}
      {editable
        && (
          <Collapse accordion>
            <Panel header={(record && record._id) ? `Documents (${record.name})` : ''} key={record._id}>
              <List
                itemLayout="horizontal"
                dataSource={documentTypes}
                renderItem={(data) => {
                  const fileList = record && record[data.key] ? [{
                    uid: record[data.key],
                    status: 'done',
                    name: record[data.key].substring(
                      record[data.key].lastIndexOf('/') + 1,
                    ),
                    url: record[data.key],
                  }] : [];

                  return (
                    <List.Item
                      actions={[
                        <Upload {...{
                          ...uploadProps,
                          data,
                          fileList,
                          onRemove: () => handleRemoveFile(data.key),
                        }}
                        >
                          {(progress > 0)
                            && (currentDocument === data.key) ? <Progress percent={progress} />
                            : !((record && record[data.key])
                              || !(record && record._id)) ? (
                              <Button
                                icon={<UploadOutlined />}
                              >
                                Upload
                              </Button>
                            ) : ''}
                        </Upload>]}
                    >
                      <List.Item.Meta
                        title={<div>{data.title}</div>}
                      />
                    </List.Item>
                  );
                }}
              />
            </Panel>
          </Collapse>
        )}
    </Form>
  );
};

MerchantForm.propTypes = {
  form: PropTypes.shape({
    current: PropTypes.shape({}),
  }),
  editable: PropTypes.bool,
  enableAffiliate: PropTypes.bool,
  selectedMerchanTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedBlacklistedProcessors: PropTypes.arrayOf(PropTypes.string).isRequired,
  handleChange: PropTypes.func.isRequired,
  handleChangeWebsites: PropTypes.func.isRequired,
  handleChangeBlacklistedProcessors: PropTypes.func.isRequired,
  websites: PropTypes.arrayOf(PropTypes.string),
};

MerchantForm.defaultProps = {
  form: {},
  // editable: false,
  enableAffiliate: false,
  websites: [],
};

export default MerchantForm;
